/**
 * Chart utility functions for ScripAnalysis.
 *
 * computeEMA   — Exponential Moving Average
 * computeRSI   — Relative Strength Index (14-period standard)
 * normalizePain — Scale total_pain values to Lightweight Charts marker size (1–5)
 */

/**
 * Compute EMA for a series of {time, close} points.
 *
 * Returns an array of {time, value} items aligned to the same dates.
 * The first (period - 1) entries are omitted (insufficient data).
 *
 * @param {Array<{time: string, close: number}>} data
 * @param {number} period
 * @returns {Array<{time: string, value: number}>}
 */
export function computeEMA(data, period) {
  if (!data || data.length < period) return [];

  const k = 2 / (period + 1);
  const result = [];
  let ema = null;

  for (let i = 0; i < data.length; i++) {
    const price = data[i].close;
    if (ema === null) {
      // Seed with simple average of first `period` points
      if (i < period - 1) continue;
      const seed = data.slice(0, period).reduce((s, d) => s + d.close, 0) / period;
      ema = seed;
    } else {
      ema = price * k + ema * (1 - k);
    }
    result.push({ time: data[i].time, value: parseFloat(ema.toFixed(2)) });
  }

  return result;
}

/**
 * Compute 14-period RSI for a series of {time, close} points.
 *
 * Uses Wilder's smoothing (same as most charting platforms).
 * Returns an array of {time, value} items; first 14 entries are omitted.
 *
 * @param {Array<{time: string, close: number}>} data
 * @param {number} [period=14]
 * @returns {Array<{time: string, value: number}>}
 */
export function computeRSI(data, period = 14) {
  if (!data || data.length <= period) return [];

  const result = [];
  let avgGain = 0;
  let avgLoss = 0;

  // Seed: simple average of first `period` gains/losses
  for (let i = 1; i <= period; i++) {
    const change = data[i].close - data[i - 1].close;
    if (change > 0) avgGain += change;
    else avgLoss += Math.abs(change);
  }
  avgGain /= period;
  avgLoss /= period;

  const rs0 = avgLoss === 0 ? 100 : avgGain / avgLoss;
  result.push({ time: data[period].time, value: parseFloat((100 - 100 / (1 + rs0)).toFixed(2)) });

  // Wilder's smoothing for subsequent values
  for (let i = period + 1; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? Math.abs(change) : 0;

    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;

    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    result.push({ time: data[i].time, value: parseFloat((100 - 100 / (1 + rs)).toFixed(2)) });
  }

  return result;
}

/**
 * Normalize total_pain values to a 3–10 range for Lightweight Charts marker sizes.
 *
 * Larger sizes (3–10) produce circles that overlap adjacent bars at the default
 * zoom level, letting 40%-transparent bubbles "meld" into each other visually.
 * Points with null total_pain receive size 4 (neutral mid-range).
 *
 * @param {Array<number|null>} pains  — raw total_pain values from the API
 * @returns {Array<number>}           — sizes in [3, 10]
 */
export function normalizePain(pains) {
  const valid = pains.filter((p) => p != null);
  if (valid.length === 0) return pains.map(() => 4);

  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min;

  return pains.map((p) => {
    if (p == null) return 4;
    if (range === 0) return 6;
    return parseFloat((3 + ((p - min) / range) * 7).toFixed(1));
  });
}

/**
 * Derive Lightweight Charts theme options from the current CSS variables.
 * Call this once per render (or on dark-mode toggle) to get a fresh options object.
 *
 * @param {boolean} isDark
 * @returns {object} Partial ChartOptions
 */
export function getLWCTheme(isDark) {
  const style = getComputedStyle(document.documentElement);
  const get = (v) => style.getPropertyValue(v).trim();

  return {
    layout: {
      background: { color: get('--surface-bg') || (isDark ? '#122833' : '#ffffff') },
      textColor:  get('--theme-text-color') || (isDark ? '#f5f5f5' : '#5f5f5f'),
    },
    grid: {
      vertLines: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
      horzLines:  { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
    },
    crosshair: {
      vertLine: { color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' },
      horzLine:  { color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' },
    },
    rightPriceScale: {
      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    },
    timeScale: {
      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    },
  };
}
